var React = require('react');
var { useState, useRef, useCallback } = require('react');

function AttachmentIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 16 16'
      aria-hidden='true'
    >
      <path
        fill='none'
        stroke='currentColor'
        strokeLinecap='round'
        d='M9.5 4v7.7c0 .8-.7 1.5-1.5 1.5s-1.5-.7-1.5-1.5V3C6.5 1.6 7.6.5 9 .5s2.5 1.1 2.5 2.5v9c0 1.9-1.6 3.5-3.5 3.5S4.5 13.9 4.5 12V4'
      />
    </svg>
  );
}

function SupportFormFields({
  t,
  isAuthenticated,
  displayName,
  email,
  message,
  attachments,
  loading,
  result,
  errorDetail,
  canSubmit,
  canAddMoreFiles,
  fileInputRef,
  onNameChange,
  onEmailChange,
  onMessageChange,
  onSubmit,
  onAddFiles,
  onFileInputChange,
  onRemoveAttachment,
  onOpenFilePicker,
  maxAttachments,
  compact,
  messageId,
}) {
  const textareaId = messageId || 'support-message';
  const [isDragOver, setIsDragOver] = useState(false);
  const dragDepthRef = useRef(0);

  const dropDisabled = loading || !canAddMoreFiles;

  const handleDragEnter = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (dropDisabled) {
        return;
      }
      dragDepthRef.current += 1;
      setIsDragOver(true);
    },
    [dropDisabled],
  );

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) {
      return;
    }
    dragDepthRef.current = 0;
    setIsDragOver(false);
  }, []);

  const handleDragEnd = useCallback(() => {
    dragDepthRef.current = 0;
    setIsDragOver(false);
  }, []);

  const handleDragOver = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (dropDisabled) {
        return;
      }
      e.dataTransfer.dropEffect = 'copy';
    },
    [dropDisabled],
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragDepthRef.current = 0;
      setIsDragOver(false);
      if (dropDisabled) {
        return;
      }
      onAddFiles(e.dataTransfer.files);
    },
    [dropDisabled, onAddFiles],
  );

  const handleDropzoneClick = useCallback(() => {
    if (!dropDisabled) {
      onOpenFilePicker();
    }
  }, [dropDisabled, onOpenFilePicker]);

  const handleDropzoneKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleDropzoneClick();
      }
    },
    [handleDropzoneClick],
  );

  return (
    <div
      className={
        compact ? 'support-widget__form-body' : 'support-page__form-body'
      }
    >
      {result === 'success' && (
        <div className='support-widget__alert support-widget__alert--success'>
          {t('support_message_sent')}
        </div>
      )}
      {result === 'error' && (
        <div className='support-widget__alert support-widget__alert--error'>
          {errorDetail || t('support_message_failed')}
        </div>
      )}

      {isAuthenticated && !email && (
        <div className='support-widget__alert support-widget__alert--warning'>
          {t('support_login_required')}
        </div>
      )}

      {!compact && (
        <p className='support-widget__intro'>{t('support_leave_message')}</p>
      )}

      <div className='support-widget__field'>
        <label className='support-widget__label'>
          <strong>{t('support_name_label')}</strong>{' '}
          {!isAuthenticated && (
            <span className='support-widget__optional'>
              ({t('support_optional')})
            </span>
          )}
        </label>
        <input
          type='text'
          className='support-widget__input'
          value={displayName}
          placeholder={t('support_name_placeholder')}
          onChange={onNameChange}
          readOnly={isAuthenticated}
          disabled={isAuthenticated}
        />
      </div>

      <div className='support-widget__field'>
        <label className='support-widget__label'>
          <strong>{t('support_email_label')}</strong>{' '}
          {!isAuthenticated && (
            <span
              className='support-widget__required'
              style={{ color: 'var(--color-danger-idel)' }}
            >
              ({t('support_required')})
            </span>
          )}
        </label>
        <input
          type='email'
          className='support-widget__input'
          value={email}
          placeholder={t('login_enter_your_email')}
          onChange={onEmailChange}
          readOnly={isAuthenticated}
          disabled={isAuthenticated}
          required
        />
      </div>

      <form onSubmit={onSubmit}>
        <div className='support-widget__field'>
          <label className='support-widget__label' htmlFor={textareaId}>
            <strong>{t('support_help_message_title')}</strong>
          </label>
          <textarea
            id={textareaId}
            className='support-widget__textarea'
            rows={compact ? 5 : 6}
            name='description'
            value={message}
            onChange={onMessageChange}
            disabled={loading}
            required
          />
        </div>

        {isAuthenticated && (
          <div className='support-widget__field'>
            <span
              className='support-widget__label'
              id='support-attachments-label'
            >
              {t('support_attachments')}
            </span>
            <div
              data-testid='drop-container'
              className={`support-widget__dropzone${isDragOver ? ' support-widget__dropzone--active' : ''}${dropDisabled ? ' support-widget__dropzone--disabled' : ''}`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
              onKeyDown={handleDropzoneKeyDown}
              tabIndex={dropDisabled ? -1 : 0}
              role='button'
              aria-labelledby='support-attachments-label'
              aria-describedby='support-attachment-hint'
            >
              <input
                ref={fileInputRef}
                id='support-file-input'
                type='file'
                multiple
                data-testid='dropzone-input'
                className='support-widget__file-input'
                onChange={onFileInputChange}
                disabled={dropDisabled}
                tabIndex={-1}
                aria-label={t('support_attachments')}
              />
              <div className='support-widget__attachment-btn'>
                <AttachmentIcon />
                <span id='support-attachment-hint'>
                  {isDragOver
                    ? t('support_drop_files')
                    : t('support_add_files', { count: maxAttachments })}
                </span>
              </div>
            </div>

            {attachments.length > 0 && (
              <ul className='support-widget__attachment-list'>
                {attachments.map((item) => (
                  <li key={item.id} className='support-widget__attachment-item'>
                    <span
                      className='support-widget__attachment-name'
                      title={item.file.name}
                    >
                      {item.file.name}
                    </span>
                    <button
                      type='button'
                      className='support-widget__attachment-remove'
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveAttachment(item.id);
                      }}
                      disabled={loading}
                      aria-label={t('support_remove_attachment')}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <button
          type='submit'
          className='support-widget__submit'
          disabled={!canSubmit}
        >
          {loading ? t('support_sending') : t('send')}
        </button>
      </form>
    </div>
  );
}

module.exports = SupportFormFields;
